<app-popover #notificationPopover [width]="'28rem'" [maxHeight]="'50vh'" [showScrollbar]="true" [placement]="'bottom'"
    [align]="'end'" [fullscreenOnMobile]="true" (onToggle)="open($event)"
    pTooltip="{{ 'Notifications & Tasks' | textCase }}" tooltipPosition="left">

    <!-- Trigger: bell icon with optional badge -->
    <ng-template #trigger>
        @if(totalCount > 0) {
        <app-icon-badge [iconType]="'bell'" [iconSource]="'lucide'" [badgeDot]="true" [iconSize]="'default'"
            [badgeSize]="'sm'" [badgePosition]="'top-right'" />
        } @else {
        <app-icon [type]="'bell'" [source]="'lucide'" [size]="'default'" />
        }
    </ng-template>

    <!-- Header slot intentionally empty -->
    <ng-template #header></ng-template>

    <!-- Body: tabs for Notifications and Tasks -->
    <ng-template #body>
        <div class="notification-body">
            <!-- Tab header row: scrollable tab nav + pinned email toggle -->
            <div class="tabs-header-row">
                <!-- p-tabs here owns only the p-tablist; panels live in a separate p-tabs below -->
                <p-tabs class="tabs-nav" [value]="activeTabIndex" (valueChange)="onTabChange($event)">
                    <p-tablist>
                        <p-tab [value]="0" class="flex flex-row justify-between items-center gap-2">
                            <h4 class="text-lg font-bold mr-2">Notifications</h4>
                            @if(notificationsCount > 0) {
                            <span class="ml-2" pBadge [value]="notificationsCount"></span>
                            }
                        </p-tab>
                        <p-tab [value]="1" class="flex flex-row justify-between items-center gap-2">
                            <h4 class="text-lg font-bold mr-2">Tasks</h4>
                            @if(jobsCount > 0 && jobFilters.selected === JOB_FILTER_STATUS.STARTED) {
                            <span pBadge [value]="jobsCount"></span>
                            }
                        </p-tab>
                    </p-tablist>
                </p-tabs>
                <!-- Toggle is a flex sibling — completely outside PrimeNG's tablist DOM -->
                <div class="tabs-email-toggle">
                    <app-icon [type]="showEmailNotifications ? 'bell-ring' : 'bell-off'" [source]="'lucide'"
                        [size]="'default'" />
                    <app-toggle [(ngModel)]="showEmailNotifications" (onChange)="onToggleEmailNotifications($event)" />
                </div>
            </div>

            <!-- Separate p-tabs that owns only the panels; activeTabIndex keeps them in sync -->
            <div class="tabs-panels-container">
                <p-tabs [value]="activeTabIndex">
                    <p-tabpanels>
                        <!-- Notifications tab -->
                        <p-tabpanel [value]="0">
                            <div class="tab-panel-items" infiniteScroll [infiniteScrollThrottle]="500"
                                (scrolled)="onNotificationScroll()" [infiniteScrollDistance]="1" [scrollWindow]="false">
                                @for (notification of notifications; track $index) {
                                <div class="rounded-base w-full p-4 border hover:bg-orange-50"
                                    [ngClass]="{'bg-white': notification.meta.isRead, 'bg-orange-50': !notification.meta.isRead, 'cursor-pointer': isNotificationClickable(notification)}"
                                    (click)="action(notification)">
                                    <div class="flex flex-row gap-4 w-full justify-between items-center">
                                        <div class="flex flex-row gap-4 justify-between items-center">
                                            <img [src]="notification.event.triggeredBy | userAvatar" alt="Avatar"
                                                class="w-8 h-8 rounded-full" />
                                            <div class="flex flex-col" style="word-break: break-word;">
                                                <span class="font-bold truncate">{{ notification.event.triggeredBy |
                                                    fullName }}</span>
                                                <div [innerHTML]="notification.meta.message" class="text-sm"
                                                    style="word-break: break-word;"></div>
                                            </div>
                                        </div>
                                        @if(!notification.meta.isRead) {
                                        <div>
                                            <span class="inline-block w-2 h-2 bg-orange-500 rounded-full"></span>
                                        </div>
                                        }
                                    </div>
                                    <div class="text-gray-500 text-xs text-right pt-2">
                                        {{ notification?.createdAt ?? '' | dateFormatter }}
                                    </div>
                                </div>
                                }
                                @if(notifications.length === 0 && !notificationLoading) {
                                <div class="flex flex-row justify-center items-center gap-4 p-4 rounded-base">
                                    <p>No notifications</p>
                                </div>
                                }
                            </div>
                            @if(notificationLoading) {
                            <div class="flex flex-row justify-center pt-4">
                                <p-progressSpinner strokeWidth="4" [style]="{width: '1.875rem', height: '1.875rem'}"
                                    fill="var(--surface-ground)" animationDuration=".5s" />
                            </div>
                            }
                        </p-tabpanel>

                        <!-- Tasks tab -->
                        <p-tabpanel [value]="1">
                            <div class="pb-4">
                                <p-chip class="mr-[0.5rem] border"
                                    [class.active-chip]="jobFilters.selected.includes(JOB_FILTER_STATUS.ALL)"
                                    [ngClass]="{'disabled-chip': jobLoading, 'cursor-pointer': !jobLoading}"
                                    (click)="chipClick(JOB_FILTER_STATUS.ALL)" label="All" />
                                <p-chip class="mr-[0.5rem] border"
                                    [ngClass]="{'disabled-chip': jobLoading, 'cursor-pointer': !jobLoading}"
                                    [class.active-chip]="jobFilters.selected.includes(JOB_FILTER_STATUS.STARTED)"
                                    (click)="chipClick(JOB_FILTER_STATUS.STARTED)" label="On Going" />
                                <p-chip class="mr-[0.5rem] border"
                                    [ngClass]="{'disabled-chip': jobLoading, 'cursor-pointer': !jobLoading}"
                                    [class.active-chip]="jobFilters.selected.includes(JOB_FILTER_STATUS.COMPLETED)"
                                    (click)="chipClick(JOB_FILTER_STATUS.COMPLETED)" label="Completed" />
                                <p-chip class="mr-[0.5rem] border"
                                    [ngClass]="{'disabled-chip': jobLoading, 'cursor-pointer': !jobLoading}"
                                    [class.active-chip]="jobFilters.selected.includes(JOB_FILTER_STATUS.FAILED)"
                                    (click)="chipClick(JOB_FILTER_STATUS.FAILED)" label="Failed" />
                            </div>

                            <div class="tab-panel-items" infiniteScroll [infiniteScrollThrottle]="500"
                                (scrolled)="onScroll()" [infiniteScrollDistance]="1" [scrollWindow]="false">
                                @for (job of jobs; track $index) {
                                <div class="job-card" [ngClass]="job.status" (click)="jobAction(job)">
                                    <div class="flex flex-row gap-4 w-full justify-between items-center">
                                        <div class="flex flex-row flex-1 gap-4 justify-between items-center">
                                            <img [src]="job.user | userAvatar" alt="Avatar"
                                                class="w-8 h-8 rounded-full" />
                                            <div class="flex flex-col flex-1">
                                                <span class="font-bold">{{ jobText(job, true) }}</span>
                                                <div class="text-sm" [pTooltip]="jobText(job)" tooltipPosition="bottom">
                                                    {{ jobText(job) | truncate:60 }}
                                                </div>
                                            </div>
                                            @if(allowCancel(job)) {
                                            <div class="ml-auto mb-auto relative" (click)="$event.stopPropagation()">
                                                <div class="cursor-pointer p-1" (click)="toggleJobMenu(job)">
                                                    <i class="pi pi-ellipsis-v"></i>
                                                </div>
                                                @if(activeJobMenu === job._id) {
                                                <div class="absolute right-0 top-full mt-1 bg-white shadow-lg rounded-base border z-[100] min-w-[8rem]">
                                                    <div class="p-3 cursor-pointer hover:bg-gray-100 flex items-center gap-2 text-red-500 text-sm rounded-base"
                                                        (click)="onCancel(job); activeJobMenu = null">
                                                        <i class="pi pi-times-circle"></i>
                                                        Cancel Task
                                                    </div>
                                                </div>
                                                }
                                            </div>
                                            }
                                        </div>
                                    </div>
                                    @if(job.status != JOB_STATUS.STARTED) {
                                    <div class="flex flex-col">
                                        <div class="text-gray-500 text-xs text-right pt-2">
                                            Start Time: {{ job?.createdAt ?? '' | dateFormatter }}
                                        </div>
                                        <div class="text-gray-500 text-xs text-right pt-2">
                                            Time Taken: {{ job?.createdAt ?? '' | duration : job.updatedAt ?? '' }}
                                        </div>
                                    </div>
                                    }
                                </div>
                                }
                                @if(jobs.length === 0 && !jobLoading) {
                                <div class="flex flex-row justify-center items-center gap-4 p-4 rounded-base">
                                    <p>No tasks present</p>
                                </div>
                                }
                            </div>
                            @if(jobLoading) {
                            <div class="flex flex-row justify-center pt-4">
                                <p-progressSpinner strokeWidth="4" [style]="{width: '1.875rem', height: '1.875rem'}"
                                    fill="var(--surface-ground)" animationDuration=".5s" />
                            </div>
                            }
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
            </div><!-- /tabs-panels-container -->

        </div>
    </ng-template>

    <!-- Footer: mark all as read -->
    <ng-template #footer>
        @if(activeTabIndex === 0) {
        <app-button label="Mark all as read" severity="contrast" variant="outlined" [disabled]="!totalCount"
            icon="pi pi-check" class="mt-3" [fullWidth]="true" (clicked)="markAllAsRead(); closePanel()" />
        }
    </ng-template>
</app-popover>
